import React, { useState, useEffect, useRef, createRef } from 'react';
import {AskConfirm, ConfirmPopup} from 'components/ConfirmPopup.jsx';
import { GetRequest, PostRequest, NumberInput, EmptyState } from 'components/common';
import { ContentPopup } from 'components/ContentPopup.jsx';

const QuoteBuilderList = ({config, setComponentShowingState, setItemIdBeingEdited, hideDeleteFromList, systemItemsFirst, hideOwnPricesList}) => {
  const default_list_path = '/quote-builder/user_unit_prices/list/settings_list/:key/:lng';
  const default_system_list_path = '/quote-builder/system_user_unit_prices/list/settings_list/:key/:lng';
  const default_delete_path = '/quote-builder/user_unit_prices/delete';
  const default_duplicate_path = '/quote-builder/user_unit_prices/duplicate';

  const STATE_EDIT  = 'edit';
  const STATE_COPY_FROM_SYSTEM  = 'copy_from_system';
  const STATE_NEW  = 'new';
  const LISTING_STATE_ALL  = 'all';

  const [listingState, setListingState] = useState(LISTING_STATE_ALL);

  const confirmPopupRef = useRef({});
  const [items, setItems] = useState([]);
  const [systemItems, setSystemItems] = useState([]);
  // Starts true so the empty state does not flash on the first render, before the mount fetch runs.
  const [fetchInProgress, setFetchInProgress] = useState(true);
  const [refreshingDataSourceId, setRefreshingDataSourceId] = useState(null);

  useEffect(() => {
    if(window.QuoteBuilderListScrollTop && listingState == STATE_EDIT) {
      $("html, body").animate({ scrollTop: 0 }, "slow");
    }
  }, [listingState])

  const fetchItems = () => {
    setFetchInProgress(true);
    setListingState(LISTING_STATE_ALL);

    GetRequest({
      url: (config.list.path || default_list_path).replace(':key', config.key).replace(':lng', window.locale),
      callback: (data) => {
        setItems(data);
        // Stay in-progress through the system-items fetch too (it clears the flag on its own callback), so the
        // empty state -- and the systemItemsFirst layout -- does not flash "no prices yet" for a round-trip
        // before the "Sık kullanılanlar" list has loaded.
        fetchSystemItems()
      }
    })
  }

  const fetchSystemItems = () => {
    setFetchInProgress(true);
    GetRequest({
      url: default_system_list_path.replace(':key', config.key).replace(':lng', window.locale),
      callback: (data) => {
        setSystemItems(data);
        setFetchInProgress(false);
      }
    })
  }

  useEffect(fetchItems, [])

  const editAction = (event, id) => {
    event.preventDefault();
    setComponentShowingState(STATE_EDIT);
    setItemIdBeingEdited(id);
  }

  const dublicateAction = (event, id) => {
    event.preventDefault();

    PostRequest({
      url: (config.list.duplicate_path || default_duplicate_path),
      data: { id: id },
      callback: (data) => {
        fetchItems();
        setFetchInProgress(false);
        document.dispatchEvent(new CustomEvent('quote_builder.user_unit_price_saved', {}) );
      },
      403: (data) => {
        alert(data.message);
      }
    })
  }

  const showPerformDeletePermissionDeniedRef = useRef();
  const showPerformDeletePermissionDenied = (message) => {
    let callback = () => {
      if (showPerformDeletePermissionDeniedRef.current) {
        showPerformDeletePermissionDeniedRef.current.style.display = "none";
      }
    };

    let response = <ContentPopup myRef={showPerformDeletePermissionDeniedRef} autoOpen={false} content={message} cancelButton={false} beforeClose={callback} />;

    return response;
  }

  const performDelete = (id) => {
    PostRequest({
      url: (config.list.delete_path || default_delete_path),
      data: { id: id },
      callback: (data) => {
        fetchItems();
        setFetchInProgress(false);
        document.dispatchEvent(new CustomEvent('quote_builder.user_unit_price_deleted', {}) );
      },
      403: (data) => {
        alert(data.message);
        // showPerformDeletePermissionDenied(data.message);
      }
    })
  }

  const deleteConfirm = (event, id) => {
    event.preventDefault();
    AskConfirm(
      confirmPopupRef,
      t('quote_builder.delete_confirm'),
       performDelete,
      id
    );
  }

  const [createBtnEnabled, setCreateBtnEnabled] = useState('disabled');
  const [createFromSystemEnabled, setCreateFromSystemEnabled] = useState(false);

  useEffect(() => {

    GetRequest({
      url: default_system_list_path.replace(':key', config.key).replace(':lng', window.locale),
      callback: (data) => {
        setCreateFromSystemEnabled(() => { return data.length > 0});
        setCreateBtnEnabled(() => { return '' });
      }
    })

  }, []);

  const newAction = (event) => {
    event.preventDefault();
    setItemIdBeingEdited(null);
    setComponentShowingState(STATE_NEW);
  }

  const defaultListTitle = () => {
    return t(`quote_builder.${config.key}_plural`)
  }

  const defaultNewTitle = () => {
    return t(`quote_builder.new_user_unit_price`).replace("%s", t(`quote_builder.${config.key}`).toLowerCase() )
  }

  const isMarkSystemEnabled = () => {
    return listingState == LISTING_STATE_ALL && config.mark_system != undefined && config.mark_system.enabled && window.mark_system_enabled;
  }

  const selectSystemAction = (e, id) => {
    e.preventDefault();
    setComponentShowingState(STATE_COPY_FROM_SYSTEM);
    setItemIdBeingEdited(id);
  }

  const listedAttributes = () => {
    return config.list.attributes;
  }

  const systemItemRow = (item) => {
    return (<tr>
      <td>
        <div className="float-left">{item.description_for_system} </div>
        <div className="float-right">
          <a href="#" data-id={item.id} className="btn btn-default simple-btn-outline" onClick={(e) => { selectSystemAction(e, item.id) } }>
            <i className="zmdi zmdi-open-in-browser"></i> {t('quote_builder.select_and_customise')}
          </a>
        </div>
      </td>
    </tr>)
  }

  const systemItemsList = () => {
    return (
      <table className="table table-striped table-bordered-outside">
        <tbody>
          <tr>
            <th colSpan={listedAttributes().length + 3}>
              <div className="float-left system-items-header">
                {config.list.title != undefined ? t(config.list.title) : defaultListTitle() } - {t(`quote_builder.listing_state_system_title`)}
              </div>
            </th>
          </tr>
          {systemItems.map(systemItemRow)}
        </tbody>
      </table>
    )
  }

  const [percentageValue, setPercentageValue] = useState(0);
  const handlePercentageValueChance = (e) => {
    let targetValue = e.target.value;
    if(targetValue < 0) { targetValue = 0;}
    setPercentageValue(targetValue);
  }

  const handleDelayedInputValuePercentage = (value) => {
    if(value == 0) { fetchItems(); return };
    if(items.length == 0) {  return };

    PostRequest({
      url: '/quote-builder/user_unit_prices/preview_percentage_change',
      data: {
        ids: checkedItemIds,
        selected_item_action: selectedItemAction,
        percentage_value: percentageValue
      },
      callback: (data) => {
        let originalItems = items;
        let attributes = listedAttributes();
        for(let j=0; j< data.length; j++) {
          for(let i=0; i< items.length; i++) {
            if(originalItems[i].id == data[j].id) {
              for(let k=0; k< attributes.length; k++) {
                let attribute = attributes[k];
                if(originalItems[i][attribute] != data[j][attribute]) {
                  originalItems[i]['alternative'] = {}
                  originalItems[i]['alternative'][attribute] = getDiff(originalItems[i][attribute], data[j][attribute]);
                }
              }

            }
          }
        }

        setItems( (_prev) => { return [ ...originalItems ] } );

      },
      403: (data) => {
        //alert(data.message);
      }
    })
  }

  const getDiff = (str1, str2) => {
    let retVal = [];
    let str1A = str1.split(" ");
    let str2A = str2.split(" ");

    for(let i=0; i < str1A.length ; i++) {
      if(str1A[i] == str2A[i]) {
        retVal.push( str1A[i] );
      } else {
        retVal.push(`<span class="percentageOld"> ${str1A[i]} </span> <span class="percentageNew"> ${str2A[i]} </span>`);
      }
    }

    return retVal.join(" ");
  }

  useEffect(() => {
    const timeOutId = setTimeout(() => handleDelayedInputValuePercentage(percentageValue), 500);
    return () => clearTimeout(timeOutId);
  }, [percentageValue]);

  const [selectedItemAction, setSelectedItemAction] = useState('increase');
  const handleSelectedItemActionChange = (e) => {
    setSelectedItemAction(e.target.value);
  }

  const [checkedItemIds, setCheckedItemIds] = useState([]);
  const handleItemChecked = (event) => {
    let id = event.target.id;
    if(event.target.checked) {
      setCheckedItemIds( (prev) => { return [...prev, id]} )
    } else {
      setCheckedItemIds( (prev) => { return prev.filter(item_id => item_id !== id)} )
    }
  }

  const handleRefreshDataSource = (event, item) => {
    event.preventDefault();
    if (!item.data_source_config) {
      return;
    }

    setRefreshingDataSourceId(item.id);
    const dataSourceConfig = item.data_source_config;

    PostRequest({
      url: `/quote-builder/data-sources/${dataSourceConfig.data_source_id}/fetch-cell`,
      data: {
        // Google Sheets / Microsoft Excel parameters
        spreadsheet_id: dataSourceConfig.spreadsheet_id,
        sheet_id: dataSourceConfig.sheet_id,
        sheet_name: dataSourceConfig.sheet_name,
        cell_reference: dataSourceConfig.cell_reference,
        // Airtable parameters
        base_id: dataSourceConfig.base_id,
        table_id: dataSourceConfig.table_id,
        record_id: dataSourceConfig.record_id,
        field_id: dataSourceConfig.field_id
      },
      callback: (data) => {
        if (!data.error && data.value !== null) {
          // Update the item in the list and save to backend
          PostRequest({
            url: `/quote-builder/user_unit_prices/refresh-data-source/${item.id}`,
            data: {
              value: data.value,
              attribute_name: dataSourceConfig.attribute_name,
              config_name: 'settings_list'
            },
            callback: (response) => {
              setRefreshingDataSourceId(null);
              if (response.item) {
                // Update just this item in the list
                setItems(prevItems => prevItems.map(i =>
                  i.id === item.id ? response.item : i
                ));
              }
            }
          });
        } else {
          setRefreshingDataSourceId(null);
        }
      }
    });
  }

  const confirmSelectedItemsAction = () => {
   if(confirm(t('quote_builder.user_unit_prices_update_confirm').replace('%s', checkedItemIds.length))) {
      PostRequest({
        url: '/quote-builder/user_unit_prices/action_percentage_change',
        data: {
          ids: checkedItemIds,
          selected_item_action: selectedItemAction,
          percentage_value: percentageValue
        },
        callback: (data) => {
          unSelectAll();
          fetchItems();

        },
        403: (data) => {
          //alert(data.message);
        }
      })
   }
  }

  useEffect( () => {
    handleDelayedInputValuePercentage(percentageValue);
  }, [selectedItemAction] )

  const confirmSelectedItemsActionDisabled = () => {
    return !(percentageValue > 0);
  }

  const unSelectAll = () => {
    $('.multi-select-chc').each(function() {
      if($(this).prop('checked')) {
        $(this).prop('checked', false);
        $(this).trigger('change');
      }
    });

    setCheckedItemIds(() => { return [] });
    setPercentageValue( () => {return 0 } );
  }

  const selectAll = () => {
    $('.multi-select-chc').each(function() {
      if(!$(this).prop('disabled')) {
        if(!$(this).prop('checked')) {
          $(this).prop('checked', true);
          $(this).trigger('change');
        }
      }
    });

    setCheckedItemIds(() => { return items.filter((item) => {return !item.is_system}).map( (item) => { return item.id } ) });
  }

  const cancelSelectedItemsAction = () => {
    unSelectAll();
    fetchItems();
  }

  const manageCheckedItemsForm = () => {
    if(checkedItemIds.length == 0) { return }
    return (
      <table className="table manageCheckedItemsTable" >
        <tbody>
         <tr>
          <td>
            <select className="form-control" name={selectedItemAction} onChange={handleSelectedItemActionChange}>
              <option value='increase'> {t('quote_builder.increase_selected_prices').replace("%s", checkedItemIds.length) }  </option>
              <option value='descrease'> {t('quote_builder.descrease_selected_prices').replace("%s", checkedItemIds.length) } </option>
            </select>
          </td>
          <td>
           <div className="input-group number_input form-short">
            {window.locale == 'tr' && <span className="input-group-addon"> <span> {t('quote_builder.percent_in_form')} </span> </span>}
            <input size="6" className="form-control" type="number" value={percentageValue} onChange={handlePercentageValueChance} step="0.1" />
            {window.locale == 'en' && <span className="input-group-addon"> <span> {t('quote_builder.percent_in_form')} </span> </span>}
            </div>

          </td>

          <td>
            <button className="btn simple-btn" disabled={confirmSelectedItemsActionDisabled()} onClick={confirmSelectedItemsAction}> <i className="fa fa-check" aria-hidden="true"></i>  {t('quote_builder.confirm')}  </button>
          </td>
          <td>
            <button className="btn" onClick={cancelSelectedItemsAction}> {t('quote_builder.cancel')}  </button>
          </td>
          </tr>
        </tbody>
      </table>
    )
  }

  const isMultiSelectEnabled = () => {
    return config.disable_multi_select_actions == undefined || config.disable_multi_select_actions == false;
  }

  const handleAllChecked = (e) => {
    // e.preventDefault();

    if(e.target.checked) {
      selectAll();
    } else {
      unSelectAll();
    }
    setAllChecked(!allChecked);
  }

  const [allChecked, setAllChecked] = useState(false)

  const renderNewButton = () => {
    return (
      <button onClick={newAction} disabled={createBtnEnabled} className="dct-button dct-button--primary">
        {createBtnEnabled == 'disabled' && (<> {t('quote_builder.please_wait') }</>) }
        {createBtnEnabled != 'disabled' && (<>
          <i className="fa fa-plus-square-o" aria-hidden="true"></i>
            {config.list.new_title!= undefined && t(config.list.new_title)}
            {config.list.new_title== undefined && defaultNewTitle()}
          </>)}
      </button>
    )
  }

  // Only after the fetch settles, so the empty state does not flash while the list loads.
  const showEmptyState = listingState == LISTING_STATE_ALL && !fetchInProgress && items.length === 0;

  // On the /mcc add-material surfaces (systemItemsFirst) an EMPTY own list leads with the "Sık kullanılanlar"
  // (system) materials, so the owner picks one to price -- "Seç ve fiyat ekle" -- instead of a blank form. The
  // own list sits BENEATH, still showing its empty-state box ("Henüz birim fiyat eklenmedi" + the add button).
  // Gated on the own list being empty: while the owner has prices (the Manage popup) their own prices stay on
  // top with the system list at the bottom; it flips to system-first only once the own list is empty --
  // including after the owner removes their last price in that popup.
  const leadWithSystemItems = systemItemsFirst && showEmptyState && systemItems.length > 0;

  // The /mcc "add a material for the manufacturing line" surface asks ONLY for a NEW material -- the owner's
  // existing prices are not the point there (their one material may be a bobbin the line cannot use), so the own
  // list is dropped. What stays is the two ways to ADD one: the "Sık kullanılanlar" system materials to copy and
  // price (Seç ve fiyat ekle) and the "Yeni birim fiyat ekle" button for a manual entry.
  if (hideOwnPricesList) {
    return (
      <>
        <ConfirmPopup myRef={confirmPopupRef} />
        <div className="myListRef">
          {systemItems.length > 0 && systemItemsList()}
          {config.list.create_enabled && (
            <div style={{ paddingTop: '15px', paddingBottom: '15px' }}>{renderNewButton()}</div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <ConfirmPopup myRef={confirmPopupRef} />
      <div className="myListRef">

        {/* systemItemsFirst + empty own list: the "Sık kullanılanlar" (system) materials lead; the own list
            (with its empty-state box) sits BENEATH (see leadWithSystemItems). Everywhere else the own list
            stays on top. */}
        {leadWithSystemItems && (systemItemsList())}

        <table className="table table-striped table-bordered-outside">
          <tbody>
          <tr>
          <th colSpan="2">
            <div className="float-left">
              <input type="checkbox"  checked={allChecked} className="all-select-chc" onChange={handleAllChecked} disabled={items.length == 0 ? 'disabled' : ''}/>  &nbsp; &nbsp;
              {t('quote_builder.my_unit_prices')}
              &nbsp;
              -
              &nbsp;
              {listingState == LISTING_STATE_ALL && config.list.title != undefined && t(config.list.title)}
              {listingState == LISTING_STATE_ALL && config.list.title == undefined && defaultListTitle()}
              &nbsp;
              {listingState == LISTING_STATE_ALL && (<>({items.length})</>) }
            </div>
          </th>
          </tr>

          {showEmptyState && (
            <EmptyState colSpan={2} message={t('quote_builder.no_unit_prices_yet')} action={config.list.create_enabled ? renderNewButton() : null} />
          )}

          {items.map((item) => {
            // The diff-preview (bulk % change) swaps a value for its highlighted before/after HTML; fall back to it.
            const attributeValue = (attribute) => (item['alternative'] != undefined && item['alternative'][attribute] != undefined) ? item['alternative'][attribute] : item[attribute];
            // First listed attribute (the description) is the card's bold heading; the rest (price, dimensions, ...)
            // read as gray chips beneath it, mirroring the "Baskı & kesim makineleri" attribute cards.
            const [headerAttribute, ...chipAttributes] = listedAttributes();
            return (
              <tr className={`${isMarkSystemEnabled() && item.is_system ? 'user-unit-price-system' : ''}`} >
                <td>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    { isMultiSelectEnabled() && (
                      <input type="checkbox" className="multi-select-chc" key={item.id} id={item.id} onChange={handleItemChecked} disabled={item.is_system ? 'disabled' : ''} style={{ marginTop: '4px' }}/>
                    )}
                    <div style={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px' }}>
                      {listingState == LISTING_STATE_ALL && headerAttribute != undefined && (
                        <span dangerouslySetInnerHTML={{__html: attributeValue(headerAttribute) }}></span>
                      )}
                      {/* Price/spec values sit inline after the name, wrapping to the next line only when the row
                          runs out of width (the long machine descriptions). A lighter, near-white fill keeps the
                          dark price text legible where the shared .attribute-tag's medium gray read like disabled
                          text; scoped here so the machines tab and library picker keep .attribute-tag for their
                          short spec labels. */}
                      {listingState == LISTING_STATE_ALL && chipAttributes.map((attribute) => (
                        <span key={attribute}
                          style={{ display: 'inline-block', fontSize: '14px', padding: '3px 9px', borderRadius: '4px', backgroundColor: '#f5f6f7', border: '1px solid #e3e6e8', color: '#333' }}
                          dangerouslySetInnerHTML={{__html: attributeValue(attribute) }}></span>
                      ))}
                    </div>
                  </div>
                </td>
                <td>
                  {listingState == LISTING_STATE_ALL && (
                    <div className="float-right" style={{ display: 'flex', gap: '14px', alignItems: 'center', whiteSpace: 'nowrap' }}>
                      {config.list.edit_enabled && <a href="#" data-id={item.id} onClick={(e) => { editAction(e, item.id) } }> <i className="fa fa-pencil-square-o" aria-hidden="true"></i> {t('quote_builder.edit')} </a> }
                      {config.list.create_enabled && <a href="#" data-id={item.id} onClick={(e) => { dublicateAction(e, item.id) } }> <i className="fa fa-files-o" aria-hidden="true"></i> {t('quote_builder.duplicate')} </a> }
                      {config.list.delete_enabled && !hideDeleteFromList && <a href="#" data-id={item.id} onClick={(e) => { deleteConfirm(e, item.id) } }> <i className="fa fa-trash-o" aria-hidden="true"></i> {t('quote_builder.delete')} </a> }
                      {config.list.delete_enabled && !hideDeleteFromList && isMarkSystemEnabled() && item.is_system && (<span> <i className="zmdi zmdi-library"></i> [kütüphane]</span>) }
                      {config.list.delete_enabled && !hideDeleteFromList && item.has_data_source && (
                        <span>
                          <a href="#" onClick={(e) => handleRefreshDataSource(e, item)} title={item.data_source_config ? `${item.data_source_config.sheet_name}!${item.data_source_config.cell_reference}` : ''}>
                            {refreshingDataSourceId === item.id ? (
                              <i className="fa fa-spinner fa-spin"></i>
                            ) : (
                              <><i className="fa fa-table" style={{ color: '#0F9D58' }}></i> <i className="fa fa-refresh"></i></>
                            )}
                          </a>
                        </span>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
          {/* When the empty-state box is shown the "add new" call to action lives inside it (below the
              message), so this bottom row is hidden to avoid a duplicate -- including when leading with the
              system list, where the empty-state box now renders beneath the system materials. */}
          {!showEmptyState && <tr>

          {config.list.create_enabled &&
            <td colSpan="2" style={{ paddingTop: '15px', paddingBottom: '15px' }}>
              <div className="float-left">
                {(manageCheckedItemsForm())}

                {renderNewButton()}
              </div>
            </td>}

          </tr>}
          </tbody>
        </table>

         {!leadWithSystemItems && systemItems.length > 0 && (systemItemsList())}
      </div>
    </>
  )
}

export default QuoteBuilderList;
